import moment from 'moment';
import { action, observable } from 'mobx';
import {
	Model,
	IModelAttributes,
	attribute,
	entity,
	ReferencePath,
} from 'Models/Model';
import * as Models from 'Models/Entities';
import * as Validators from 'Validators';
import { CRUD } from '../CRUDOptions';
import * as AttrUtils from 'Util/AttributeUtils';
import { IAcl } from 'Models/Security/IAcl';
import {
	makeFetchManyToManyFunc,
	makeJoinEqualsFunc,
	makeFetchOneToManyFunc,
	getCreatedModifiedCrudOptions,
} from 'Util/EntityUtils';
import UserMonitored from 'Models/Security/Acl/UserMonitored';
import { EntityFormMode } from 'Views/Components/Helpers/Common';
import SuperAdministratorScheme from '../Security/Acl/SuperAdministratorScheme';

import BusinessDetailEntity, { IBusinessDetailEntityAttributes } from './BusinessDetailEntity';

export interface IMonitoredEntityAttributes extends IModelAttributes {
	legalName: string;
	businessName: string;
	abn: string;
	acn: string;
	asicextractdate: Date;

	atoDebt?: Models.AtoDebtEntity |
	Models.IAtoDebtEntityAttributes;
	atoRecordss: Array<
		| Models.AtoRecordEntity
		| Models.IAtoRecordEntityAttributes
	>;
	alaresReport?: Models.AlaresReportEntity |
	Models.IAlaresReportEntityAttributes;
	creditReports: Array<
		| Models.CreditReportEntity
		| Models.ICreditReportEntityAttributes
	>;
	customerss: Array<
		| Models.MonitoredsCustomers
		| Models.IMonitoredsCustomersAttributes
	>;
	directors: Array<
		| Models.DirectorEntity
		| Models.IDirectorEntityAttributes
	>;
	monitoredentityaudits: Array<
		| Models.MonitoredentityauditEntity
		| Models.IMonitoredentityauditEntityAttributes
	>;

	businessDetail?: BusinessDetailEntity | IBusinessDetailEntityAttributes;
}

@entity('MonitoredEntity', 'Monitored')
export default class MonitoredEntity extends Model
	implements IMonitoredEntityAttributes {
	public static acls: IAcl[] = [
		new SuperAdministratorScheme(),
		new UserMonitored(),
	];

	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	@observable
	@attribute<MonitoredEntity, string>()
	@CRUD({
		name: 'Legal Name',
		displayType: 'textfield',
		order: 10,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public legalName: string;

	@observable
	@attribute<MonitoredEntity, string>()
	@CRUD({
		name: 'Business Name',
		displayType: 'textfield',
		order: 20,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public businessName: string;

	@Validators.Unique()
	@observable
	@attribute<MonitoredEntity, string>()
	@CRUD({
		name: 'ABN',
		displayType: 'textfield',
		order: 30,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public abn: string;

	@Validators.Unique()
	@observable
	@attribute<MonitoredEntity, string>()
	@CRUD({
		name: 'ACN',
		displayType: 'textfield',
		order: 120,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public acn: string;

	@observable
	@attribute<MonitoredEntity, Date>()
	@CRUD({
		name: 'ASICExtractDate',
		displayType: 'datetimepicker',
		order: 200,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseDate,
	})
	public asicextractdate: Date;

	@observable
	@attribute({ isReference: true, manyReference: false })
	@CRUD({

		name: 'ATO Debt',
		displayType: 'displayfield',
		order: 210,
		inputProps: {
			displayFunction: (model?: Models.AtoDebtEntity) => model ? model.getDisplayName() : null,
		},
	})
	public atoDebt?: Models.AtoDebtEntity;

	@observable
	@attribute({ isReference: true, manyReference: true })
	@CRUD({

		name: 'ATO Recordss',
		displayType: 'reference-multicombobox',
		order: 220,
		referenceTypeFunc: () => Models.AtoRecordEntity,
		disableDefaultOptionRemoval: true,
		referenceResolveFunction: makeFetchOneToManyFunc({
			relationName: 'atoRecordss',
			oppositeEntity: () => Models.AtoRecordEntity,
		}),
	})
	public atoRecordss: Models.AtoRecordEntity[] = [];

	@observable
	@attribute({ isReference: true, manyReference: false })
	@CRUD({

		name: 'Alares Report',
		displayType: 'displayfield',
		order: 230,
		inputProps: {
			displayFunction: (model?: Models.AlaresReportEntity) => model ? model.getDisplayName() : null,
		},
	})
	public alaresReport?: Models.AlaresReportEntity;

	@observable
	@attribute({ isReference: true, manyReference: true })
	@CRUD({
		name: 'Credit Reports',
		displayType: 'reference-multicombobox',
		order: 240,
		referenceTypeFunc: () => Models.CreditReportEntity,
		referenceResolveFunction: makeFetchOneToManyFunc({
			relationName: 'creditReports',
			oppositeEntity: () => Models.CreditReportEntity,
		}),
	})
	public creditReports: Models.CreditReportEntity[] = [];

	@observable
	@attribute({ isReference: true, manyReference: true })
	@CRUD({

		name: 'Customers',
		displayType: 'reference-multicombobox',
		order: 250,
		isJoinEntity: true,
		referenceTypeFunc: () => Models.MonitoredsCustomers,
		optionEqualFunc: makeJoinEqualsFunc('customersId'),
		referenceResolveFunction: makeFetchManyToManyFunc({
			entityName: 'monitoredEntity',
			oppositeEntityName: 'customerEntity',
			relationName: 'monitoreds',
			relationOppositeName: 'customers',
			entity: () => Models.MonitoredEntity,
			joinEntity: () => Models.MonitoredsCustomers,
			oppositeEntity: () => Models.CustomerEntity,
		}),
	})
	public customerss: Models.MonitoredsCustomers[] = [];

	@observable
	@attribute({ isReference: true, manyReference: true })
	@CRUD({
		name: 'Directors',
		displayType: 'reference-multicombobox',
		order: 260,
		referenceTypeFunc: () => Models.DirectorEntity,
		disableDefaultOptionRemoval: true,
		referenceResolveFunction: makeFetchOneToManyFunc({
			relationName: 'directors',
			oppositeEntity: () => Models.DirectorEntity,
		}),
	})
	public directors: Models.DirectorEntity[] = [];

	@observable
	@attribute({ isReference: true, manyReference: true })
	@CRUD({

		name: 'MonitoredEntityAudits',
		displayType: 'reference-multicombobox',
		order: 270,
		referenceTypeFunc: () => Models.MonitoredentityauditEntity,
		disableDefaultOptionRemoval: true,
		referenceResolveFunction: makeFetchOneToManyFunc({
			relationName: 'monitoredentityaudits',
			oppositeEntity: () => Models.MonitoredentityauditEntity,
		}),

	})
	public monitoredentityaudits: Models.MonitoredentityauditEntity[] = [];

	@observable
	@attribute({ isReference: true, manyReference: false })
	@CRUD({
		name: 'Business Detail',
		displayType: 'displayfield',
		order: 280,
		inputProps: {
			displayFunction: (model?: BusinessDetailEntity) => model ? model.getDisplayName() : null,
		},
	})
	public businessDetail?: BusinessDetailEntity;

	constructor(attributes?: Partial<IMonitoredEntityAttributes>) {
		super(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	@action
	public assignAttributes(attributes?: Partial<IMonitoredEntityAttributes>) {

		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.legalName !== undefined) {
				this.legalName = attributes.legalName;
			}
			if (attributes.businessName !== undefined) {
				this.businessName = attributes.businessName;
			}
			if (attributes.abn !== undefined) {
				this.abn = attributes.abn;
			}
			if (attributes.acn !== undefined) {
				this.acn = attributes.acn;
			}
			if (attributes.asicextractdate !== undefined) {
				if (attributes.asicextractdate === null) {
					this.asicextractdate = attributes.asicextractdate;
				} else {
					this.asicextractdate = moment(attributes.asicextractdate).toDate();
				}
			}
			if (attributes.atoDebt !== undefined) {
				if (attributes.atoDebt === null) {
					this.atoDebt = attributes.atoDebt;
				} else if (attributes.atoDebt instanceof Models.AtoDebtEntity) {
					this.atoDebt = attributes.atoDebt;
				} else {
					this.atoDebt = new Models.AtoDebtEntity(attributes.atoDebt);
				}
			}
			if (attributes.atoRecordss !== undefined && Array.isArray(attributes.atoRecordss)) {
				for (const model of attributes.atoRecordss) {
					if (model instanceof Models.AtoRecordEntity) {
						this.atoRecordss.push(model);
					} else {
						this.atoRecordss.push(new Models.AtoRecordEntity(model));
					}
				}
			}
			if (attributes.alaresReport !== undefined) {
				if (attributes.alaresReport === null) {
					this.alaresReport = attributes.alaresReport;
				} else if (attributes.alaresReport instanceof Models.AlaresReportEntity) {
					this.alaresReport = attributes.alaresReport;
				} else {
					this.alaresReport = new Models.AlaresReportEntity(attributes.alaresReport);
				}
			}
			if (attributes.creditReports !== undefined && Array.isArray(attributes.creditReports)) {
				for (const model of attributes.creditReports) {
					if (model instanceof Models.CreditReportEntity) {
						this.creditReports.push(model);
					} else {
						this.creditReports.push(new Models.CreditReportEntity(model));
					}
				}
			}
			if (attributes.customerss !== undefined && Array.isArray(attributes.customerss)) {
				for (const model of attributes.customerss) {
					if (model instanceof Models.MonitoredsCustomers) {
						this.customerss.push(model);
					} else {
						this.customerss.push(new Models.MonitoredsCustomers(model));
					}
				}
			}
			if (attributes.directors !== undefined && Array.isArray(attributes.directors)) {
				for (const model of attributes.directors) {
					if (model instanceof Models.DirectorEntity) {
						this.directors.push(model);
					} else {
						this.directors.push(new Models.DirectorEntity(model));
					}
				}
			}
			if (attributes.monitoredentityaudits !== undefined && Array.isArray(attributes.monitoredentityaudits)) {
				for (const model of attributes.monitoredentityaudits) {
					if (model instanceof Models.MonitoredentityauditEntity) {
						this.monitoredentityaudits.push(model);
					} else {
						this.monitoredentityaudits.push(new Models.MonitoredentityauditEntity(model));
					}
				}
			}
			if (attributes.businessDetail !== undefined) {
				if (attributes.businessDetail === null) {
					this.businessDetail = attributes.businessDetail;
				} else if (attributes.businessDetail instanceof BusinessDetailEntity) {
					this.businessDetail = attributes.businessDetail;
				} else {
					this.businessDetail = new BusinessDetailEntity(attributes.businessDetail);
				}
			}

		}
	}

	/**
	 * Additional fields that are added to GraphQL queries when using the
	 * the managed model APIs.
	 */

	public defaultExpands = `
		customerss {
			${Models.MonitoredsCustomers.getAttributes().join('\n')}
			customers {
				${Models.CustomerEntity.getAttributes().join('\n')}
			}
		}
		atoDebt {
			${Models.AtoDebtEntity.getAttributes().join('\n')}
		}
		atoRecordss {
			${Models.AtoRecordEntity.getAttributes().join('\n')}
		}
		alaresReport {
			${Models.AlaresReportEntity.getAttributes().join('\n')}
		}
		creditReports {
			${Models.CreditReportEntity.getAttributes().join('\n')}
		}
		directors {
			${Models.DirectorEntity.getAttributes().join('\n')}
		}
		monitoredentityaudits {
			${Models.MonitoredentityauditEntity.getAttributes().join('\n')}
		}
	`;
	/**
		 * The save method that is called from the admin CRUD components.
		 */

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	public async saveFromCrud(formMode: EntityFormMode) {
		const relationPath: ReferencePath = {
			customerss: {},
			atoRecordss: {},
			creditReports: {},
			directors: {},
			monitoredentityaudits: {},
		};
		return this.save(
			relationPath,
			{
				options: [
					{
						key: 'mergeReferences',
						graphQlType: '[String]',
						value: [
							'creditReports',
							'customerss',
						],
					},
				],
			},
		);
	}

	/**
	 * Returns the string representation of this entity to display on the UI.
	 */
	public getDisplayName() {
		return this.id;
	}
}

/*
 * Retrieve the created and modified CRUD attributes for defining the CRUD views and decorate the class with them.
 */
const [createdAttr, modifiedAttr] = getCreatedModifiedCrudOptions();
CRUD(createdAttr)(MonitoredEntity.prototype, 'created');
CRUD(modifiedAttr)(MonitoredEntity.prototype, 'modified');
